import React from 'react';

const FileScanResults = ({ files, scanResults, onScan, isScanning, onRemoveFile }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (result) => {
    if (result.exists) {
      return <i className="bi bi-exclamation-triangle-fill text-warning"></i>;
    }
    return <i className="bi bi-check-circle-fill text-success"></i>;
  };

  const getStatusText = (result) => {
    if (result.exists) {
      return 'File already exists';
    }
    return 'Ready to upload';
  };

  const getStatusBadgeClass = (result) => {
    if (result.exists) {
      return 'bg-warning';
    }
    return 'bg-success';
  };

  if (isScanning) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5>Scanning Files...</h5>
        <p className="text-muted">
          Checking for duplicate files and analyzing content...
        </p>
        <div className="progress mt-3" style={{ height: '6px' }}>
          <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
        </div>
      </div>
    );
  }

  if (!scanResults) {
    return (
      <div className="text-center py-4">
        <h5 className="mb-3">
          <i className="bi bi-search me-2"></i>
          File Analysis
        </h5>
        <p className="text-muted mb-4">
          We'll check your selected files for duplicates and analyze their content before uploading.
        </p>
        <div className="list-group">
          {files.map((file, index) => (
            <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <i className="bi bi-file-earmark me-3 text-primary"></i>
                <div>
                  <div className="fw-medium">{file.name}</div>
                  <small className="text-muted">
                    {formatFileSize(file.size)}
                  </small>
                </div>
              </div>
              <span className="badge bg-secondary">Pending scan</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const duplicateCount = scanResults.filter(r => r.exists).length;
  const readyCount = scanResults.filter(r => !r.exists).length;

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="card border-success h-100">
            <div className="card-body py-2 px-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                <span className="fw-medium me-2">{readyCount}</span>
                <small className="text-muted">Ready to Upload</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-warning h-100">
            <div className="card-body py-2 px-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                <span className="fw-medium me-2">{duplicateCount}</span>
                <small className="text-muted">Duplicates Found</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h6 className="mb-3">
        <i className="bi bi-list-check me-2"></i>
        Scan Results
      </h6>

      <div className="list-group">
        {scanResults.map((result, index) => (
          <div key={index} className="list-group-item">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-start flex-grow-1">
                <div className="me-3 mt-1">
                  {getStatusIcon(result)}
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium">{result.file.name}</div>
                  <small className="text-muted">
                    {formatFileSize(result.file.size)} • {result.file.type || 'Unknown type'}
                  </small>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${getStatusBadgeClass(result)}`}>
                  {getStatusText(result)}
                </span>
                {result.exists && onRemoveFile && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => onRemoveFile(result.file)}
                    title="Remove this file from upload"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {duplicateCount > 0 && (
        <div className="alert alert-warning mt-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> Some files may already exist in our knowledge base. 
          Uploading again will replace the "knowledge" from the old file with the new file (and cost money). 
          If you want to maintain both files in the knowledge, please use a new filename.
          {onRemoveFile && (
            <div className="mt-2">
              <small>
                <i className="bi bi-info-circle me-1"></i>
                You can remove problematic files from the upload by clicking the trash icon next to them.
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileScanResults;
